/**
 * CardInfoPanel — Standardised card detail display
 *
 * Consolidates card-info sections across QRCodeGenerator, Sales,
 * Landing, Trade, and Search & Browse into one component.
 *
 * Variants:
 *  - "compact"  — small inline row (search results, trade items, QR lists)
 *  - "detailed" — full panel with larger image + grid of fields (modals, details panes)
 */

import { hasCardNumber } from '../../utils/cardNumber'
import React, { useEffect, useState } from 'react'
import { CardImageModal } from '../modals'
import { useCurrency } from '../../contexts/CurrencyContext'
import { formatSetCode } from '../../utils/setCode'
import styles from './CardInfoPanel.module.css'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface CardInfoPanelProps {
  /** Display mode */
  variant?: 'compact' | 'detailed'
  compactNameSize?: 'default' | 'large'
  compactMetaLayout?: 'inline' | 'stacked'
  compactImageSize?: 'default' | 'large'
  compactPricePlacement?: 'side' | 'below' | 'below-compact'

  /* Core fields */
  name: string
  imageUrl?: string | null
  setName?: string | null
  setCode?: string | null
  setNumber?: string | null
  setSymbolUrl?: string | null
  rarity?: string | null
  printing?: string | null
  condition?: string | null
  gameName?: string | null
  series?: string | null
  releaseDate?: string | null
  /** Card artist. Populated for Magic from Scryfall's `artist` field; often absent elsewhere. */
  illustrator?: string | null

  /** Price in cents — omit to hide */
  priceCents?: number | null
  /** Currency symbol/label shown before the price */
  currency?: string
  /** Show a "$0" warning indicator */
  priceWarning?: boolean

  /** Render numeric price yourself (overrides priceCents formatting) */
  priceLabel?: string | null

  /** Extra badge labels (e.g. "3 variants", "Qty 5") */
  badges?: string[]

  /** Click handler on the whole panel */
  onClick?: () => void

  /** Optional click handlers for set/game chips (prevents panel click bubbling) */
  onSetNameClick?: () => void
  onSetCodeClick?: () => void
  onGameNameClick?: () => void

  /** Extra content rendered after the core fields (slots) */
  children?: React.ReactNode

  className?: string
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

// formatCents is no longer used — useCurrency().formatPrice handles conversion

function normalizeRarityToken(rarity?: string | null): string {
  return String(rarity || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

const RARITY_DISPLAY_MAP: Record<string, string> = {
  c: 'Common',
  u: 'Uncommon',
  uc: 'Uncommon',
  r: 'Rare',
  rr: 'Double Rare',
  dr: 'Double Rare',
  tr: 'Triple Rare',
  sr: 'Super Rare',
  spr: 'Special Rare',
  scr: 'Secret Rare',
  sec: 'Secret Rare',
  ur: 'Ultra Rare',
  gr: 'Gold Rare',
  gdr: 'God Rare',
  zr: 'Z-Rare',
  zl: 'Z-Leader',
  l: 'Leader',
  pr: 'Promo',
  p: 'Promo',
  ex: 'Extra Rare',
  cr: 'Campaign Rare',
  cp: 'Campaign Rare',
}

export function formatRarityLabel(rarity?: string | null): string {
  const raw = String(rarity || '').trim()
  if (!raw) return ''

  const normalized = normalizeRarityToken(raw)
  return RARITY_DISPLAY_MAP[normalized] || raw
}

export function getRarityClassName(rarity?: string | null): string {
  const normalized = normalizeRarityToken(rarity)

  if (!normalized) return 'rarityDefault'
  if (normalized.includes('showcase')) return 'rarityShowcase'
  if (normalized.includes('promo')) return 'rarityPromo'
  if (normalized.includes('epic')) return 'rarityEpic'
  if (normalized.includes('mythic')) return 'rarityMythic'
  if (normalized.includes('legendary')) return 'rarityLegendary'
  if (normalized.includes('secret') || normalized === 'scr' || normalized === 'sec') return 'raritySecret'
  if (
    normalized.includes('ultra')
    || normalized.includes('double rare')
    || normalized.includes('super rare')
    || normalized.includes('special rare')
    || normalized.includes('god rare')
    || normalized.includes('z rare')
    || normalized === 'rr'
    || normalized === 'dr'
    || normalized === 'sr'
    || normalized === 'spr'
    || normalized === 'gdr'
    || normalized === 'zr'
    || normalized.includes('special illustration')
  ) return 'rarityUltra'
  if (normalized.includes('rare') || normalized === 'r' || normalized === 'tr') return 'rarityRare'
  if (normalized.includes('uncommon') || normalized === 'u' || normalized === 'uc') return 'rarityUncommon'
  if (normalized.includes('common') || normalized === 'c') return 'rarityCommon'

  return 'rarityDefault'
}

export function shouldRenderRarity(rarity?: string | null): boolean {
  const normalized = normalizeRarityToken(rarity)
  if (!normalized) return false
  if (normalized === 'none') return false
  if (normalized === 'unknown' || normalized === 'n a' || normalized === 'na' || normalized === '-') return false
  return true
}

function normalizeGameToken(gameName?: string | null): string {
  return String(gameName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
}

export function getGameAccentClassName(gameName?: string | null): string {
  const normalized = normalizeGameToken(gameName)

  if (!normalized) return 'gameAccentDefault'
  if (normalized.includes('digimon')) return 'gameAccentDigimon'
  if (normalized.includes('dragon ball') || normalized.includes('fusion world') || normalized.includes('dragonball')) return 'gameAccentDragonBall'
  if (normalized.includes('flesh and blood') || normalized === 'fab') return 'gameAccentFleshBlood'
  if (normalized.includes('final fantasy') || normalized.includes('fftcg')) return 'gameAccentFinalFantasy'
  if (normalized.includes('pokemon') && (normalized.includes('japan') || normalized.includes('japanese'))) return 'gameAccentPokemonJapan'
  if (normalized.includes('pokemon')) return 'gameAccentPokemon'
  if (normalized.includes('cardfight') || normalized.includes('vanguard')) return 'gameAccentVanguard'
  if (normalized.includes('weiss') || normalized.includes('schwarz')) return 'gameAccentWeissSchwarz'
  if (normalized.includes('yu gi oh') || normalized.includes('yugioh')) return 'gameAccentYugioh'
  if (normalized.includes('magic') || normalized.includes('mtg') || normalized.includes('commander')) return 'gameAccentMagic'
  if (normalized.includes('one piece')) return 'gameAccentOnePiece'
  if (normalized.includes('lorcana') || normalized.includes('disney')) return 'gameAccentLorcana'
  if (normalized.includes('riftbound') || normalized.includes('league of legends')) return 'gameAccentRiftbound'

  return 'gameAccentDefault'
}

/** Deterministic hash for a string → positive integer */
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (Math.imul(31, hash) + str.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

/**
 * Returns inline CSS custom-property overrides that tint set-name and
 * set-code chips with a unique hue per set code.  The hue is derived
 * deterministically from the set-code string so the same set always gets the
 * same colour.  Text colour is intentionally left unset so it inherits from
 * the game-accent class (which is already dark-mode–aware).
 */
export function getSetAccentStyle(setCode?: string | null): React.CSSProperties {
  if (!setCode) return {}
  const hash = hashString(setCode.trim().toUpperCase())
  // Build a hue that avoids the brand red range (330–360 / 0–25°)
  const rawHue = hash % 360
  const hue = rawHue < 25 ? rawHue + 42 : rawHue > 330 ? rawHue - 48 : rawHue
  const sat = 58 + ((hash >> 10) % 20) // 58–78 %
  const lightBorder = 32 + ((hash >> 16) % 14) // 32–45 %
  const darkBorder = 70 + ((hash >> 18) % 12) // 70–81 %
  const lightText = `hsl(${hue}, ${Math.max(62, sat)}%, 26%)`
  const darkText = `hsl(${hue}, ${Math.max(70, sat)}%, 84%)`
  return {
    '--set-accent-bg-light': `hsla(${hue}, ${sat}%, 52%, 0.14)`,
    '--set-accent-border-light': `hsla(${hue}, ${sat}%, ${lightBorder}%, 0.42)`,
    '--set-accent-text-light': lightText,
    '--set-accent-bg-dark': `hsla(${hue}, ${Math.max(64, sat)}%, 60%, 0.24)`,
    '--set-accent-border-dark': `hsla(${hue}, ${Math.max(64, sat)}%, ${darkBorder}%, 0.52)`,
    '--set-accent-text-dark': darkText,
  } as React.CSSProperties
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const CardInfoPanel: React.FC<CardInfoPanelProps> = ({
  variant = 'compact',
  compactNameSize = 'default',
  compactMetaLayout = 'inline',
  compactImageSize = 'default',
  compactPricePlacement = 'side',
  name,
  imageUrl,
  setName,
  setCode,
  setNumber,
  setSymbolUrl,
  rarity,
  printing,
  condition,
  gameName,
  series,
  releaseDate,
  illustrator,
  priceCents,
  currency: _currency,
  priceWarning,
  priceLabel,
  badges,
  onClick,
  onSetNameClick,
  onSetCodeClick,
  onGameNameClick,
  children,
  className = '',
}) => {
  const isDetailed = variant === 'detailed'
  const [imageFailed, setImageFailed] = useState(false)
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const { formatPrice } = useCurrency()
  const gameAccentClassName = styles[getGameAccentClassName(gameName)]
  const setAccentStyle = getSetAccentStyle(setCode)
  const displaySetCode = formatSetCode(setCode)

  useEffect(() => {
    setImageFailed(false)
  }, [imageUrl])

  const containerCls = [
    styles.container,
    isDetailed ? styles.detailed : styles.compact,
    onClick ? styles.clickable : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const displayPrice =
    priceLabel != null
      ? priceLabel
      : typeof priceCents === 'number' && priceCents > 0
        ? formatPrice(priceCents)
        : null
  const rarityLabel = formatRarityLabel(rarity)

  /* ---- Compact layout ---- */
  if (!isDetailed) {
    const showSidePrice = displayPrice !== null && compactPricePlacement !== 'below'
    const showBelowPrice = displayPrice !== null && compactPricePlacement !== 'side'



    const setAndGameChips = (
      <>
        {setName && (
          onSetNameClick ? (
            <button
              type="button"
              className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.setChip} ${styles.metaChipLink}`}
              style={setAccentStyle}
              onClick={(event) => {
                event.stopPropagation()
                onSetNameClick()
              }}
              title={`Filter by set: ${setName}`}
            >
              {setName}
            </button>
          ) : (
            <span className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.setChip}`} style={setAccentStyle}>
              {setName}
            </span>
          )
        )}
        {displaySetCode && (
          onSetCodeClick ? (
            <button
              type="button"
              className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.setCodeChip} ${styles.metaChipLink}`}
              style={setAccentStyle}
              onClick={(event) => {
                event.stopPropagation()
                onSetCodeClick()
              }}
              title={`Filter by set code: ${displaySetCode}`}
            >
              {displaySetCode}
            </button>
          ) : (
            <span className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.setCodeChip}`} style={setAccentStyle}>
              {displaySetCode}
            </span>
          )
        )}
        {gameName && (
          onGameNameClick ? (
            <button
              type="button"
              className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.gameChip} ${gameAccentClassName} ${styles.metaChipLink}`}
              onClick={(event) => {
                event.stopPropagation()
                onGameNameClick()
              }}
              title={`Filter by game: ${gameName}`}
            >
              {gameName}
            </button>
          ) : (
            <span className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.gameChip} ${gameAccentClassName}`}>
              {gameName}
            </span>
          )
        )}
      </>
    )

    return (
      <div className={containerCls} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
        {imageUrl && !imageFailed ? (
          <button
            type="button"
            className={`${styles.compactImage} ${compactImageSize === 'large' ? styles.compactImageLarge : ''} ${styles.zoomableImage}`}
            data-card-image-trigger="true"
            onClick={(e) => {
              e.stopPropagation()
              e.nativeEvent.stopImmediatePropagation()
              setImageModalOpen(true)
            }}
            aria-label={`Zoom image for ${name}`}
          >
            <img
              src={imageUrl}
              alt={name}
              loading="lazy"
              decoding="async"
              width={compactImageSize === 'large' ? 72 : 56}
              height={compactImageSize === 'large' ? 98 : 77}
              onError={() => setImageFailed(true)}
            />
          </button>
        ) : (
          <div className={`${styles.compactImage} ${compactImageSize === 'large' ? styles.compactImageLarge : ''} ${styles.compactImageEmpty}`}>
            <span>No Image</span>
          </div>
        )}

        <div className={`${styles.compactInfo} ${compactMetaLayout === 'stacked' ? styles.compactInfoComfortable : ''}`}>
          {compactMetaLayout === 'stacked' ? (
            <>
              {/* Line 1: Name + set number + rarity */}
              <div className={styles.compactNameLine}>
                <span className={`${styles.compactName} ${compactNameSize === 'large' ? styles.compactNameLarge : ''}`.trim()}>{name}</span>
                {hasCardNumber(setNumber) && (
                  <span className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.metaChipNumberPill}`}>
                    #{setNumber}
                  </span>
                )}
                {shouldRenderRarity(rarityLabel) && (
                  <span className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles[getRarityClassName(rarityLabel)]}`}>
                    {rarityLabel}
                  </span>
                )}
              </div>
              {/* Line 2: Set name */}
              {(setName || displaySetCode) && (() => {
                const combinedSetLabel = setName
                  ? (displaySetCode ? `${setName} (${displaySetCode})` : setName)
                  : displaySetCode || null
                const combinedClickHandler = onSetNameClick || onSetCodeClick
                return combinedSetLabel ? (
                  <div className={`${styles.compactMeta} ${styles.compactMetaRow}`}>
                    {combinedClickHandler ? (
                      <button
                        type="button"
                        className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.setChip} ${styles.metaChipLink}`}
                        style={setAccentStyle}
                        onClick={(event) => { event.stopPropagation(); combinedClickHandler() }}
                        title={`Filter by set: ${combinedSetLabel}`}
                      >
                        {combinedSetLabel}
                      </button>
                    ) : (
                      <span className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.setChip}`} style={setAccentStyle}>
                        {combinedSetLabel}
                      </span>
                    )}
                  </div>
                ) : null
              })()}
              {/* Line 3: Game name */}
              {gameName && (
                <div className={`${styles.compactMeta} ${styles.compactMetaRow}`}>
                  {onGameNameClick ? (
                    <button
                      type="button"
                      className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.gameChip} ${gameAccentClassName} ${styles.metaChipLink}`}
                      onClick={(event) => { event.stopPropagation(); onGameNameClick() }}
                      title={`Filter by game: ${gameName}`}
                    >
                      {gameName}
                    </button>
                  ) : (
                    <span className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles.gameChip} ${gameAccentClassName}`}>
                      {gameName}
                    </span>
                  )}
                </div>
              )}
</>
          ) : (
            <>
              <div className={`${styles.compactName} ${compactNameSize === 'large' ? styles.compactNameLarge : ''}`.trim()}>{name}</div>
              <div className={styles.compactMeta}>
                {hasCardNumber(setNumber) && <span className={`${styles.metaChip} ${styles.metaChipNumber}`}>#{setNumber}</span>}
                {setAndGameChips}
                {shouldRenderRarity(rarityLabel) && (
                  <span className={`${styles.metaChip} ${styles.metaChipNoDivider} ${styles.metaChipPill} ${styles[getRarityClassName(rarityLabel)]}`}>
                    {rarityLabel}
                  </span>
                )}
              </div>
            </>
          )}

          {(printing || condition) && (
            <div className={styles.compactVariant}>
              {printing && <span>{printing}</span>}
              {condition && <span>{condition}</span>}
            </div>
          )}

          {badges && badges.length > 0 && (
            <div className={styles.compactBadges}>
              {badges.map((b, i) => (
                <span key={i} className={styles.badge}>{b}</span>
              ))}
            </div>
          )}
        </div>

        {showBelowPrice && (
          <div
            className={[
              styles.compactPrice,
              styles.compactPriceBubble,
              compactPricePlacement === 'below-compact' ? styles.compactPriceCompactOnly : styles.compactPriceBelow,
            ].filter(Boolean).join(' ')}
          >
            <span className={priceWarning ? styles.priceWarn : ''}>{displayPrice}</span>
          </div>
        )}

        {showSidePrice && (
          <div
            className={[
              styles.compactPrice,
              compactPricePlacement === 'below-compact' ? styles.compactPriceDesktopOnly : '',
            ].filter(Boolean).join(' ')}
          >
            <span className={priceWarning ? styles.priceWarn : ''}>{displayPrice}</span>
          </div>
        )}

        {children}

        <CardImageModal
          imageUrl={imageUrl && !imageFailed ? imageUrl : null}
          alt={name}
          isOpen={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
        />
      </div>
    )
  }

  /* ---- Detailed layout ---- */
  return (
    <div className={containerCls} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
      <div className={styles.detailedMedia}>
        {imageUrl && !imageFailed ? (
          <button
            type="button"
            className={styles.zoomableImage}
            data-card-image-trigger="true"
            onClick={(e) => {
              e.stopPropagation()
              setImageModalOpen(true)
            }}
            aria-label={`Zoom image for ${name}`}
          >
            <img src={imageUrl} alt={name} className={styles.detailedImage} loading="lazy" onError={() => setImageFailed(true)} />
          </button>
        ) : (
          <div className={styles.detailedImageEmpty}>No Image</div>
        )}
      </div>

      <div className={styles.detailedBody}>
        <h3 className={styles.detailedName}>{name}</h3>

        <div className={styles.detailedGrid}>
          {setName && (
            <div className={styles.gridItem}>
              <span className={styles.gridLabel}>Set</span>
              <span className={styles.gridValue}>
                {setSymbolUrl && <img src={setSymbolUrl} alt="" className={styles.setSymbol} loading="lazy" decoding="async" />}
                {setName}
                {displaySetCode && <span className={styles.setCodeTag}>{displaySetCode}</span>}
              </span>
            </div>
          )}

          {hasCardNumber(setNumber) && (
            <div className={styles.gridItem}>
              <span className={styles.gridLabel}>Number</span>
              <span className={styles.gridValue}>{setNumber}</span>
            </div>
          )}

          {gameName && (
            <div className={styles.gridItem}>
              <span className={styles.gridLabel}>Game</span>
              <span className={styles.gridValue}>{gameName}</span>
            </div>
          )}

          {series && (
            <div className={styles.gridItem}>
              <span className={styles.gridLabel}>Series</span>
              <span className={styles.gridValue}>{series}</span>
            </div>
          )}

          {releaseDate && (
            <div className={styles.gridItem}>
              <span className={styles.gridLabel}>Release Date</span>
              <span className={styles.gridValue}>
                {new Date(releaseDate).toLocaleDateString()}
              </span>
            </div>
          )}

          {shouldRenderRarity(rarityLabel) && (
            <div className={styles.gridItem}>
              <span className={styles.gridLabel}>Rarity</span>
              <span className={styles.gridValue}>{rarityLabel}</span>
            </div>
          )}

          {printing && (
            <div className={styles.gridItem}>
              <span className={styles.gridLabel}>Printing</span>
              <span className={styles.gridValue}>{printing}</span>
            </div>
          )}

          {condition && (
            <div className={styles.gridItem}>
              <span className={styles.gridLabel}>Condition</span>
              <span className={styles.gridValue}>{condition}</span>
            </div>
          )}

          {illustrator && (
            <div className={styles.gridItem}>
              <span className={styles.gridLabel}>Illustrator</span>
              <span className={styles.gridValue}>{illustrator}</span>
            </div>
          )}

          {displayPrice !== null && (
            <div className={styles.gridItem}>
              <span className={styles.gridLabel}>Price</span>
              <span className={`${styles.gridValue} ${priceWarning ? styles.priceWarn : styles.priceGood}`}>
                {displayPrice}
              </span>
            </div>
          )}
        </div>

        {badges && badges.length > 0 && (
          <div className={styles.detailedBadges}>
            {badges.map((b, i) => (
              <span key={i} className={styles.badge}>{b}</span>
            ))}
          </div>
        )}

        {children}
      </div>

      <CardImageModal
        imageUrl={imageUrl && !imageFailed ? imageUrl : null}
        alt={name}
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
      />
    </div>
  )
}

export default CardInfoPanel
